import { useCallback, useEffect, useRef, useState } from "react"
import { useForm } from "react-hook-form"
import { Form } from "~/shadcn/ui/form"
import {
  SearchValues,
  searchResolver,
  searchDefaultValues,
} from "~/screens/casting/data/searchSchema"
import { Button } from "~/shadcn/ui/button"
import {
  activeFilterTypes,
  castingCallFilterType,
  changedFilters,
} from "~/util/filterTracking"
import { trackEvent } from "~/util/tracking"
import { AllFiltersButton } from "./all-filters/AllFiltersButton"
import { AllFiltersModal } from "./all-filters/AllFiltersModal"

export const SearchBar = ({
  setSearchValues,
  initialValues,
  myRoster = false,
}: {
  setSearchValues: (values: SearchValues) => void
  initialValues?: SearchValues
  myRoster?: boolean
}) => {
  const [filterModalOpen, setFilterModalOpen] = useState(false)
  const form = useForm<SearchValues>({
    resolver: searchResolver,
    defaultValues: searchDefaultValues,
  })
  // The modal applies every dimension at once, so the analytics events are
  // derived by comparing each applied search with the previous one.
  const appliedValues = useRef<SearchValues>(searchDefaultValues)
  const allFiltersApplied = useRef(false)

  const applySearch = useCallback(
    (values: SearchValues) => {
      setFilterModalOpen(false)
      setSearchValues(values)

      for (const change of changedFilters(
        appliedValues.current,
        values,
        castingCallFilterType
      )) {
        trackEvent("filter_casting_calls", change)
      }

      if (allFiltersApplied.current) {
        allFiltersApplied.current = false
        const active = activeFilterTypes(values, castingCallFilterType)
        trackEvent("apply_all_filters", {
          filters_count: active.length,
          active_filters: active.join(", "),
        })
      }

      appliedValues.current = values
    },
    [setSearchValues]
  )

  const onClear = () => {
    form.reset(searchDefaultValues)
    applySearch({})
    trackEvent("clear_filters", { source: "casting_calls" })
  }

  const onSubmit = form.handleSubmit(applySearch, (error) => {
    console.error(error)
  })

  useEffect(() => {
    if (initialValues) {
      form.reset({ ...searchDefaultValues, ...initialValues })
      applySearch({ ...searchDefaultValues, ...initialValues })
    }
  }, [form, applySearch, initialValues])

  return (
    <Form {...form}>
      <div className="flex-grow flex flex-row justify-stretch gap-4 max-w-5xl">
        <AllFiltersButton
          onClick={() => {
            setFilterModalOpen(true)
            trackEvent("open_all_filters_modal", { source: "casting_calls" })
          }}
        />
        <Button
          type="button"
          variant="linkSecondary"
          size="none"
          onClick={onClear}
        >
          Clear all
        </Button>
      </div>
      <AllFiltersModal
        open={filterModalOpen}
        setOpen={setFilterModalOpen}
        onClear={onClear}
        onSubmit={() => {
          allFiltersApplied.current = true
          onSubmit()
        }}
        myRoster={myRoster}
      />
    </Form>
  )
}
